import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import KPICard from "../components/KPICard";
import { StatusBadge } from "../components/Badges";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend, LabelList,
} from "recharts";
import {
  Briefcase, FolderOpen, UserCheck, PauseOctagon, AlertTriangle,
  TrendingUp, Download, PlayCircle, Ban, UserMinus, Calendar, X, Gift,
} from "lucide-react";

const COLORS = ["#EC4899", "#A855F7", "#06B6D4", "#84CC16", "#FB923C", "#F472B6", "#FACC15", "#8B5CF6"];
const GRAD = {
  pink: ["#EC4899", "#F472B6"],
  purple: ["#A855F7", "#8B5CF6"],
  cyan: ["#06B6D4", "#0EA5E9"],
  lime: ["#84CC16", "#A3E635"],
  tangerine: ["#FB923C", "#F59E0B"],
  sunshine: ["#FACC15", "#FBBF24"],
};

function GradientDefs() {
  return (
    <defs>
      {Object.entries(GRAD).map(([id, [a, b]]) => (
        <linearGradient key={id} id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={a} stopOpacity={1} />
          <stop offset="100%" stopColor={b} stopOpacity={0.85} />
        </linearGradient>
      ))}
      {Object.entries(GRAD).map(([id, [a, b]]) => (
        <linearGradient key={`h-${id}`} id={`g-${id}-h`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={a} stopOpacity={0.85} />
          <stop offset="100%" stopColor={b} stopOpacity={1} />
        </linearGradient>
      ))}
      <linearGradient id="g-bar-h" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#06B6D4" stopOpacity={1} />
        <stop offset="100%" stopColor="#A855F7" stopOpacity={1} />
      </linearGradient>
    </defs>
  );
}

// ---- Date hierarchy helpers (calendar year drill-down) ----
const MONTHS = [
  { v: 1, label: "January" }, { v: 2, label: "February" }, { v: 3, label: "March" },
  { v: 4, label: "April" }, { v: 5, label: "May" }, { v: 6, label: "June" },
  { v: 7, label: "July" }, { v: 8, label: "August" }, { v: 9, label: "September" },
  { v: 10, label: "October" }, { v: 11, label: "November" }, { v: 12, label: "December" },
];
const QUARTERS = [
  { v: 1, label: "Q1 (Jan – Mar)", months: [1, 2, 3] },
  { v: 2, label: "Q2 (Apr – Jun)", months: [4, 5, 6] },
  { v: 3, label: "Q3 (Jul – Sep)", months: [7, 8, 9] },
  { v: 4, label: "Q4 (Oct – Dec)", months: [10, 11, 12] },
];

function buildYears() {
  const cy = new Date().getFullYear();
  const list = [];
  for (let y = cy + 1; y >= cy - 4; y--) list.push(y);
  return list;
}

function pad(n) { return String(n).padStart(2, "0"); }

function daysInMonth(y, m1to12) {
  return new Date(y, m1to12, 0).getDate();
}

function deriveRange({ year, quarter, month, week }) {
  if (!year) return { from: "", to: "" };
  const y = parseInt(year, 10);
  if (year && quarter && month && week) {
    const m = parseInt(month, 10);
    const w = parseInt(week, 10);
    const startDay = (w - 1) * 7 + 1;
    const endDay = Math.min(w * 7, daysInMonth(y, m));
    return {
      from: `${y}-${pad(m)}-${pad(startDay)}`,
      to: `${y}-${pad(m)}-${pad(endDay)}`,
    };
  }
  if (year && quarter && month) {
    const m = parseInt(month, 10);
    return {
      from: `${y}-${pad(m)}-01`,
      to: `${y}-${pad(m)}-${pad(daysInMonth(y, m))}`,
    };
  }
  if (year && quarter) {
    const q = QUARTERS.find((x) => x.v === parseInt(quarter, 10));
    if (q) {
      const sm = q.months[0];
      const em = q.months[2];
      return {
        from: `${y}-${pad(sm)}-01`,
        to: `${y}-${pad(em)}-${pad(daysInMonth(y, em))}`,
      };
    }
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

export default function Analytics() {
  const nav = useNavigate();
  const [brand, setBrand] = useState("All");
  // Date hierarchy
  const [year, setYear] = useState("");
  const [quarter, setQuarter] = useState("");
  const [month, setMonth] = useState("");
  const [week, setWeek] = useState("");
  // Filters (multi-select arrays)
  const [recruiter, setRecruiter] = useState([]);
  const [businessUnit, setBusinessUnit] = useState([]);
  const [region, setRegion] = useState([]);
  const [budgetType, setBudgetType] = useState([]);
  const [productBrand, setProductBrand] = useState([]);
  const [country, setCountry] = useState([]);
  const [city, setCity] = useState([]);
  const [hiringManager, setHiringManager] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [entities, setEntities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [budgetTypes, setBudgetTypes] = useState([]);
  const [productBrands, setProductBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  const [stats, setStats] = useState(null);
  const [positionsList, setPositionsList] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const years = useMemo(() => buildYears(), []);
  const { from: dateFrom, to: dateTo } = useMemo(
    () => deriveRange({ year, quarter, month, week }),
    [year, quarter, month, week]
  );

  useEffect(() => {
    const params = { only_active: true };
    Promise.all([
      api.get("/masters/recruiters", { params }),
      api.get("/masters/entities", { params }),
      api.get("/masters/regions", { params }),
      api.get("/masters/business_units", { params }),
      api.get("/masters/budget_types", { params }),
      api.get("/masters/brands", { params }),
      api.get("/masters/countries", { params }),
      api.get("/masters/cities", { params }),
    ]).then(([r, e, rg, bu, bt, pb, co, ci]) => {
      setRecruiters(r.data); setEntities(e.data); setRegions(rg.data);
      setBusinessUnits(bu.data); setBudgetTypes(bt.data);
      setProductBrands(pb.data); setCountries(co.data); setCities(ci.data);
    });
  }, []);

  const load = async () => {
    const joined = (arr) => (arr && arr.length ? arr.join(",") : undefined);
    const r = await api.get("/positions/stats", {
      params: {
        brand,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        recruiter: joined(recruiter),
        business_unit: joined(businessUnit),
        region: joined(region),
        budget_type: joined(budgetType),
        product_brand: joined(productBrand),
        country: joined(country),
        city: joined(city),
        hiring_manager: joined(hiringManager),
      },
    });
    setStats(r.data);
  };

  const loadPositionsList = async () => {
    setPositionsLoading(true);
    try {
      const joined = (arr) => (arr && arr.length ? arr.join(",") : undefined);
      const r = await api.get("/positions", {
        params: {
          brand,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          recruiter: joined(recruiter),
          business_unit: joined(businessUnit),
          region: joined(region),
          budget_type: joined(budgetType),
          city: joined(city),
          hiring_manager: joined(hiringManager),
          page: 1, page_size: 500,
          sort_by: "created_at", sort_dir: -1,
        },
      });
      setPositionsList(r.data.items);
    } finally { setPositionsLoading(false); }
  };

  useEffect(() => { load(); loadPositionsList(); /* eslint-disable-next-line */ }, [brand, dateFrom, dateTo, recruiter, businessUnit, region, budgetType, productBrand, country, city, hiringManager]);

  const downloadPdf = async () => {
    const r = await api.get("/reports/analytics/pdf", {
      params: { brand, date_from: dateFrom || undefined, date_to: dateTo || undefined },
      responseType: "blob",
    });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `Analytics_${brand}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setYear(""); setQuarter(""); setMonth(""); setWeek("");
    setRecruiter([]); setBusinessUnit([]); setRegion([]); setBudgetType([]);
    setProductBrand([]); setCountry([]); setCity([]); setHiringManager([]);
  };

  const hasFilters = year || recruiter.length || businessUnit.length || region.length || budgetType.length || productBrand.length || country.length || city.length || hiringManager.length;
  const monthOptions = quarter ? QUARTERS.find((q) => q.v === parseInt(quarter, 10))?.months || [] : [];

  if (!stats) return <div className="text-muted-foreground">Loading…</div>;

  const monthlyData = Object.entries(stats.monthly_closures).map(([m, v]) => ({ month: m, closures: v }));

  // New: closed bifurcation by city / department / source / region (Closed + Closed-Resigned)
  const toClosedRows = (obj) => Object.entries(obj || {})
    .map(([name, v]) => ({ name, Closed: v.Closed || 0, "Closed-Resigned": v["Closed-Resigned"] || 0, total: (v.Closed || 0) + (v["Closed-Resigned"] || 0) }))
    .filter((d) => d.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);
  const cityData = toClosedRows(stats.closed_by_city);
  const deptData = toClosedRows(stats.closed_by_department);
  const sourceData = toClosedRows(stats.closed_by_source);
  const regionData = toClosedRows(stats.closed_by_region);

  // Open positions by recruiter
  const recruiterData = Object.entries(stats.open_recruiter_breakdown || {})
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);

  // Status display order for the bottom Positions table
  const STATUS_ORDER = ["Closed", "To Start", "Closed-Resigned", "Open", "On Hold", "Offer Declined", "Cancelled"];
  const sortedPositions = [...positionsList].sort((a, b) => {
    const ia = STATUS_ORDER.indexOf(a.status);
    const ib = STATUS_ORDER.indexOf(b.status);
    const ai = ia === -1 ? STATUS_ORDER.length : ia;
    const bi = ib === -1 ? STATUS_ORDER.length : ib;
    if (ai !== bi) return ai - bi;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Insights</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Analytics</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Entity</Label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="analytics-brand-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                {entities.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={downloadPdf} className="h-8 text-xs" size="sm" data-testid="export-pdf-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Compact filter bar — single line on desktop */}
      <div className="rounded-xl border border-border bg-card px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {/* Combined Date hierarchy (Popover) */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-xs font-normal"
                data-testid="analytics-date-trigger"
              >
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                {(() => {
                  if (!year) return "Date: All time";
                  const parts = [year];
                  if (quarter) parts.push(`Q${quarter}`);
                  if (month) parts.push(MONTHS.find((x) => x.v === parseInt(month, 10))?.label?.slice(0, 3));
                  if (week) parts.push(`W${week}`);
                  return `Date: ${parts.join(" · ")}`;
                })()}
                {(year || quarter || month || week) && (
                  <X
                    className="w-3 h-3 ml-1.5 opacity-60 hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); setYear(""); setQuarter(""); setMonth(""); setWeek(""); }}
                    data-testid="analytics-date-clear"
                  />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" align="start" data-testid="analytics-date-popover">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">
                Drill Down
              </div>
              <div className="space-y-2.5">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Year</Label>
                  <Select value={year || "all"} onValueChange={(v) => { const nv = v === "all" ? "" : v; setYear(nv); if (!nv) { setQuarter(""); setMonth(""); setWeek(""); } }}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="analytics-year"><SelectValue placeholder="All years" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All years</SelectItem>
                      {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Quarter</Label>
                  <Select
                    value={quarter || "all"}
                    onValueChange={(v) => { const nv = v === "all" ? "" : v; setQuarter(nv); if (!nv) { setMonth(""); setWeek(""); } }}
                    disabled={!year}
                  >
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="analytics-quarter"><SelectValue placeholder={year ? "All quarters" : "Pick year first"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All quarters</SelectItem>
                      {QUARTERS.map((q) => <SelectItem key={q.v} value={String(q.v)}>{q.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Month</Label>
                  <Select
                    value={month || "all"}
                    onValueChange={(v) => { const nv = v === "all" ? "" : v; setMonth(nv); if (!nv) setWeek(""); }}
                    disabled={!quarter}
                  >
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="analytics-month"><SelectValue placeholder={quarter ? "All months" : "Pick quarter"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All months</SelectItem>
                      {monthOptions.map((m) => {
                        const mo = MONTHS.find((x) => x.v === m);
                        return <SelectItem key={m} value={String(m)}>{mo?.label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Week</Label>
                  <Select
                    value={week || "all"}
                    onValueChange={(v) => setWeek(v === "all" ? "" : v)}
                    disabled={!month}
                  >
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="analytics-week"><SelectValue placeholder={month ? "All weeks" : "Pick month"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All weeks</SelectItem>
                      <SelectItem value="1">Week 1 (1–7)</SelectItem>
                      <SelectItem value="2">Week 2 (8–14)</SelectItem>
                      <SelectItem value="3">Week 3 (15–21)</SelectItem>
                      <SelectItem value="4">Week 4 (22–28)</SelectItem>
                      <SelectItem value="5">Week 5 (29–end)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(dateFrom || dateTo) && (
                  <div className="pt-2 border-t border-border text-[10px] text-muted-foreground">
                    <span className="font-medium text-foreground">{dateFrom || "—"}</span> → <span className="font-medium text-foreground">{dateTo || "—"}</span>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Pivot filters — compact multi-select */}
          <MultiSelectFilter
            label="Recruiter"
            value={recruiter}
            options={recruiters.map((r) => r.name)}
            onChange={setRecruiter}
            testId="analytics-recruiter"
            width="w-[140px]"
          />
          <MultiSelectFilter
            label="Hiring Manager"
            value={hiringManager}
            options={Object.keys(stats?.hiring_manager_breakdown || {}).filter((c) => c && c !== "?").sort()}
            onChange={setHiringManager}
            testId="analytics-hiring-manager"
            width="w-[160px]"
          />
          <MultiSelectFilter
            label="Business Unit"
            value={businessUnit}
            options={businessUnits.map((b) => b.name)}
            onChange={setBusinessUnit}
            testId="analytics-business-unit"
            width="w-[150px]"
          />
          <MultiSelectFilter
            label="Region"
            value={region}
            options={regions.map((r) => r.name)}
            onChange={setRegion}
            testId="analytics-region"
            width="w-[120px]"
          />
          <MultiSelectFilter
            label="Budget Type"
            value={budgetType}
            options={budgetTypes.map((b) => b.name)}
            onChange={setBudgetType}
            testId="analytics-budget-type"
            width="w-[140px]"
          />
          <MultiSelectFilter
            label="Brand"
            value={productBrand}
            options={productBrands.map((b) => b.name)}
            onChange={setProductBrand}
            testId="analytics-product-brand"
            width="w-[130px]"
          />
          <MultiSelectFilter
            label="Country"
            value={country}
            options={countries.map((c) => c.name)}
            onChange={setCountry}
            testId="analytics-country"
            width="w-[130px]"
          />
          <MultiSelectFilter
            label="City"
            value={city}
            options={cities.map((c) => c.name)}
            onChange={setCity}
            testId="analytics-city"
            width="w-[120px]"
          />

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="h-8 px-2 text-xs" data-testid="analytics-clear-filters">
              Clear all
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <KPICard label="Total" value={stats.total} icon={Briefcase} accent="primary" />
        <KPICard label="Open" value={stats.open} icon={FolderOpen} accent="blue" />
        <KPICard label="Closed" value={stats.closed} icon={UserCheck} accent="emerald" />
        <KPICard label="On Hold" value={stats.on_hold} icon={PauseOctagon} accent="amber" />
        <KPICard label="To Start" value={stats.to_start ?? 0} icon={PlayCircle} accent="violet" />
        <KPICard label="Cancelled" value={stats.cancelled ?? 0} icon={Ban} accent="red" />
        <KPICard label="Closed-Resigned" value={stats.closed_resigned ?? 0} icon={UserMinus} accent="slate" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <KPICard label="Recruitment Rate" value={`${stats.recruitment_rate}%`} icon={TrendingUp} accent="emerald" />
        <KPICard label="Offer Declined" value={stats.offer_declined ?? 0} icon={AlertTriangle} accent="red" />
        <KPICard
          label="Referral Bonus"
          value={`₹${(stats.referral_bonus_total ?? 0).toLocaleString("en-IN")}`}
          icon={Gift}
          accent="violet"
        />
      </div>

      {(stats.alerts.open_over_30 + stats.alerts.offer_no_doj_15 + stats.alerts.trf_on_hold) > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          <AlertCard label="Open > 30 days" value={stats.alerts.open_over_30} />
          <AlertCard label="Offer with no DOJ > 15 days" value={stats.alerts.offer_no_doj_15} />
          <AlertCard label="TRF On Hold" value={stats.alerts.trf_on_hold} />
        </div>
      )}

      {/* 2 charts per line */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Open Positions by Recruiter">
          {recruiterData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={recruiterData} layout="vertical" margin={{ top: 8, right: 32, left: 0, bottom: 0 }} barSize={18}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(168, 85, 247, 0.12)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} width={120} />
                <Tooltip cursor={{ fill: "rgba(168, 85, 247, 0.06)" }} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Bar dataKey="value" fill="url(#g-bar-h)" radius={[14, 14, 14, 14]}>
                  <LabelList dataKey="value" position="right" fill="#475569" fontSize={11} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Monthly Closures">
          {monthlyData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={monthlyData} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(168, 85, 247, 0.15)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Line
                  type="monotone" dataKey="closures"
                  stroke="url(#g-bar-h)" strokeWidth={4}
                  dot={{ r: 6, fill: "#A855F7", strokeWidth: 3, stroke: "#fff" }}
                  activeDot={{ r: 8, fill: "#EC4899", strokeWidth: 3, stroke: "#fff" }}
                >
                  <LabelList dataKey="closures" position="top" fill="#475569" fontSize={11} fontWeight={700} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Closed Positions by City">
          {cityData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={cityData} layout="vertical" margin={{ top: 8, right: 36, left: 0, bottom: 0 }} barSize={20}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(6, 182, 212, 0.12)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} width={110} />
                <Tooltip cursor={{ fill: "rgba(6, 182, 212, 0.06)" }} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Closed" stackId="a" fill="url(#g-cyan-h)" radius={[12, 0, 0, 12]} />
                <Bar dataKey="Closed-Resigned" stackId="a" fill="url(#g-pink-h)" radius={[0, 12, 12, 0]}>
                  <LabelList dataKey="total" position="right" fill="#475569" fontSize={11} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Closed Positions by Department">
          {deptData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={deptData} layout="vertical" margin={{ top: 8, right: 36, left: 0, bottom: 0 }} barSize={20}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(132, 204, 22, 0.12)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} width={170} />
                <Tooltip cursor={{ fill: "rgba(132, 204, 22, 0.06)" }} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Closed" stackId="a" fill="url(#g-lime-h)" radius={[12, 0, 0, 12]} />
                <Bar dataKey="Closed-Resigned" stackId="a" fill="url(#g-purple-h)" radius={[0, 12, 12, 0]}>
                  <LabelList dataKey="total" position="right" fill="#475569" fontSize={11} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Closed Positions by Source">
          {sourceData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={sourceData} margin={{ top: 28, right: 16, left: 0, bottom: 0 }} barSize={32}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(251, 146, 60, 0.15)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B", fontWeight: 600 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(251, 146, 60, 0.08)" }} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Closed" stackId="a" fill="url(#g-tangerine)" />
                <Bar dataKey="Closed-Resigned" stackId="a" fill="url(#g-purple)" radius={[14, 14, 0, 0]}>
                  <LabelList dataKey="total" position="top" fill="#475569" fontSize={11} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Closed Positions by Region">
          {regionData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={regionData} margin={{ top: 28, right: 16, left: 0, bottom: 0 }} barSize={48}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(250, 204, 21, 0.18)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B", fontWeight: 600 }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(250, 204, 21, 0.10)" }} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Closed" stackId="a" fill="url(#g-sunshine)" />
                <Bar dataKey="Closed-Resigned" stackId="a" fill="url(#g-pink)" radius={[16, 16, 0, 0]}>
                  <LabelList dataKey="total" position="top" fill="#475569" fontSize={11} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Positions list table */}
      <Panel title={`Positions (${positionsList.length})`}>
        <div className="-mx-5 -mb-5 max-h-[26rem] overflow-y-auto overflow-x-auto scrollbar-thin border-t border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-2.5 whitespace-nowrap">ID</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Position</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Recruiter</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Status</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Department</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Location</th>
                <th className="px-4 py-2.5 whitespace-nowrap">Entity</th>
              </tr>
            </thead>
            <tbody data-testid="analytics-positions-table">
              {positionsLoading && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!positionsLoading && sortedPositions.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                  No positions match the current filters.
                </td></tr>
              )}
              {sortedPositions.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => nav(`/positions/${p.id}`)}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                  data-testid={`analytics-row-${p.id}`}
                >
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-primary whitespace-nowrap">{p.position_code || "—"}</td>
                  <td className="px-4 py-2.5 font-medium text-foreground whitespace-nowrap">
                    {p.designation}
                    {p.candidate_name && (
                      <span className="text-xs text-muted-foreground ml-2">· {p.candidate_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-foreground/80 whitespace-nowrap">{p.recruiter || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2.5 text-foreground/80 whitespace-nowrap">{p.department || "—"}</td>
                  <td className="px-4 py-2.5 text-foreground/80 whitespace-nowrap">
                    {[p.city || p.location, p.country].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{p.brand || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-4 font-semibold">{title}</div>
      {children}
    </div>
  );
}

function AlertCard({ label, value }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
        <AlertTriangle className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-amber-900/80 font-medium uppercase tracking-widest">{label}</div>
        <div className="font-heading text-2xl font-semibold text-amber-900">{value}</div>
      </div>
    </div>
  );
}

function Empty() {
  return <div className="h-[320px] grid place-items-center text-sm text-muted-foreground">No data yet</div>;
}
